import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Mail, Check, ArrowLeft } from 'lucide-react';
import { forgotPassword } from '../lib/api';

/**
 * `/forgot-password` — the first half of the password reset flow.
 *
 * User enters their account email. We POST to `/api/auth/forgot-password`
 * which always returns 200 (silent 404 on unknown addresses so we don't
 * leak account existence). Regardless of the result, we show the same
 * "check your email" success card — no user-visible branching.
 *
 * Companion route: `/reset-password?token=...` (see ResetPassword.jsx).
 */
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErr('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await forgotPassword(trimmed);
      setSent(true);
    } catch (e2) {
      // Endpoint is designed to always 200; a genuine failure here means
      // the network / server is down. Surface a soft error so the user
      // can retry, but keep the copy generic.
      setErr(e2?.response?.data?.detail || e2.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-10" data-testid="forgot-password-page">
      <button
        onClick={() => navigate('/')}
        data-testid="forgot-password-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 mb-6"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex items-center gap-2 mb-3">
        <Mail size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="forgot-password-title">
          Forgot your password?
        </h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Enter the email address you used to sign up and we&apos;ll send you a link to choose a new password.
      </p>

      {sent ? (
        <div
          className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-200"
          data-testid="forgot-password-sent"
        >
          <div className="flex items-start gap-3">
            <Check size={20} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold mb-1">Check your inbox.</div>
              <div className="text-sm">
                If <span className="font-medium">{email.trim()}</span> is registered, a reset link is on its way.
                The link expires in 30 minutes.
              </div>
              <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-3">
                Didn&apos;t get one? Wait a minute, then <Link to="/forgot-password" onClick={() => { setSent(false); setEmail(''); }} className="underline">try again</Link>.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="fp-email" className="block text-sm font-medium mb-1">Email address</label>
            <input
              id="fp-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-password-email-input"
              placeholder="you@example.com"
              className="w-full p-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          {err && (
            <div
              className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm"
              data-testid="forgot-password-error"
            >
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            data-testid="forgot-password-submit-btn"
            className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold w-full sm:w-auto"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
            Send reset link
          </button>
        </form>
      )}

      <div className="mt-6 text-xs text-zinc-500">
        Remembered it? <Link to="/" className="text-amber-600 hover:underline">Back to sign in</Link>.
      </div>
    </div>
  );
};

export default ForgotPassword;
